import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {IDeviceHardwareStore} from "../../common/interfaces/IDeviceHardwareStore";
import {IDeviceInfo} from "../../common/interfaces/IDeviceInfo";
import {IDeviceBrowserStore} from "../../common/interfaces/IDeviceBrowserStore";

// Define store interface
export interface IDeviceDebug {
  // if device is mocked for dev
  isMocked?: boolean;
}

export interface IDeviceStore {
  info?: IDeviceInfo;
  browser?: IDeviceBrowserStore;
  hardware?: IDeviceHardwareStore;
  debug?: IDeviceDebug;
}

const initialState: IDeviceStore = {};

export const deviceSlice = createSlice({
  name: `device`,
  initialState,

  reducers: (create) => ({
    deviceReducer: create.reducer((store: IDeviceStore, action: PayloadAction<IDeviceStore>) => {
      const {info, browser, debug, hardware} = action.payload;

      const isStoreMockedBefore = store.debug?.isMocked;

      // if device prior mocked and not updating with new mock we don't update
      const shouldReturnWithoutUpdate: boolean = (
        // store was mocked before this update
        (// update hasn't passed as mocked
          (!!isStoreMockedBefore && !debug?.isMocked))
      );

      if (shouldReturnWithoutUpdate ) {
        // shouldn't update, return unchanged
        return;
      }

      store.info = info;
      store.browser = browser;
      store.hardware = hardware;
      store.debug = debug;
    }),

    mockedDeviceReducer: create.reducer((state, action: PayloadAction<IDeviceInfo>) => {
      state.info = action.payload;
      state.debug = {
        ...state.debug,
        isMocked: true,
      }
    }),

    mockedHardwareReducer: create.reducer((state, action: PayloadAction<IDeviceHardwareStore>) => {
      state.hardware = action.payload;
      state.debug = {
        ...state.debug,
        isMocked: true,
      }
    }),

    mockedBrowserReducer: create.reducer((state, action: PayloadAction<IDeviceBrowserStore>) => {
      state.browser = action.payload;
      state.debug = {
        ...state.debug,
        isMocked: true,
      }
    })
  }),
  selectors: {
    deviceBrowserNameSelector: (state: IDeviceStore)=> state.browser?.name,
    deviceBrowserSelector: (state: IDeviceStore)=> state.browser,
    deviceBrowserVersionSelector: (state: IDeviceStore)=> state.browser?.version,
    deviceHardwareSelector: (state: IDeviceStore) => state.hardware ?? {},
    deviceInfoSelector: (state: IDeviceStore) => state.info ?? {},
    deviceInfoVersionSelector: (state: IDeviceStore)=> state.info?.deviceInfoVersion,
    deviceIsDesktopSelector: (state: IDeviceStore)=> !!state.info?.isDesktop,
    deviceIsMobileSelector: (state: IDeviceStore)=> !!state.info?.isMobile,
    deviceIsTabletSelector: (state: IDeviceStore)=> !!state.info?.isPad,
    deviceOsSelector: (state: IDeviceStore)=> state.info?.os,
    deviceOsVersionSelector: (state: IDeviceStore)=> state.info?.osVersion,
  }
});

export const deviceReducer = deviceSlice.reducer;
